import React, { useMemo, useState } from 'react';
import { alpha, Box, ButtonBase, Collapse, Divider, Stack, Theme } from '@mui/material';
import { AdditionalData } from 'app/concierge/types';
import { docsSectionHeader, domainSectionHeader, SectionDomain } from 'app/concierge/hooks/docsAnswer';
import IconHandler, { IconType } from 'components/common/icon/IconHandler';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { renderMarkdown } from 'tools/safeHtml';
import ResultItems from '../result-items';
import useResultItemsData, { renderableResults } from '../result-items/useResultItemsData';
import defaultStyles from '../styles';

interface ISectionProps {
    title: string;
    icons?: IconType[];
    dataTest: string;
    defaultExpanded?: boolean;
    children: React.ReactNode;
}

/** The blocks' icons and their Display Names: the same label whether the section opens or only names its sources. */
function SectionLabel({ title, icons = [], dataTest }: { title: string; icons?: IconType[]; dataTest: string }) {
    return (
        <>
            {icons.map((icon, index) => (
                <Box
                    sx={{
                        mt: '1px',
                        width: '16px',
                        height: '16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '.svg-icon-mi': { fontSize: '16px' },
                        '.svg-inline--fa': { fontSize: '14px' },
                        img: {
                            maxWidth: '100%',
                            maxHeight: '100%',
                        },
                    }}
                >
                    <IconHandler key={index} icon={icon} data-test={`${dataTest}_icon`} />
                </Box>
            ))}
            <Box className="section-label-title" sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.8) }}>
                {title}
            </Box>
        </>
    );
}

const labelSx = {
    px: 0.75,
    py: '3px',
    border: '1px solid transparent',
    borderRadius: '6px',
    display: 'inline-flex',
    gap: 1,
    fontSize: '12px',
    color: 'text.primary',
    // A title naming several blocks may wrap; keep it flush left like the other sections.
    justifyContent: 'flex-start',
    textAlign: 'left',
} as const;

function CollapsibleSection({ title, icons = [], dataTest, defaultExpanded = false, children }: ISectionProps) {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <Box>
            <Box
                sx={{
                    ...labelSx,
                    ...{
                        '&:hover': {
                            cursor: 'pointer',
                            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                            borderColor: (theme) => alpha(theme.palette.text.primary, 0.16),
                            '.section-label-title': {
                                color: (theme) => alpha(theme.palette.text.primary, 1),
                            },
                        },
                    },
                }}
                data-test={dataTest}
                onClick={() => setExpanded((prev) => !prev)}
            >
                <SectionLabel title={title} icons={icons} dataTest={dataTest} />
                <IconMi icon={expanded ? 'chevron-up' : 'chevron-down'} sx={{ fontSize: '16px', mt: '1px' }} />
            </Box>
            {/* mountOnEnter defers the section body (and its data fetches) until the first expand */}
            <Collapse in={expanded} mountOnEnter>
                {children}
            </Collapse>
        </Box>
    );
}

function DashedDivider() {
    return (
        <Divider
            sx={{
                borderStyle: 'dashed',
                borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.24),
            }}
        />
    );
}

interface IProps {
    data: AdditionalData;
    /** The panel's Concierge blocks: their Display Names and icons title the sections. */
    domains?: SectionDomain[];
    /** Reports already shown as the answer's source chips: never repeated here (Marius, 2026-09-08). */
    excludeElementIds?: number[];
}

export default function AdditionalDataSections({ data, domains = [], excludeElementIds = [] }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    // The tile lookups run as soon as the results arrive, not on the first expand, and the
    // section exists only when at least one row can render: a header over nothing is never shown.
    // Rows naming a report that is already a source chip are dropped before the count and the lookup.
    const excludeKey = excludeElementIds.join(',');
    const searchItems = useMemo(() => {
        const excluded = new Set(excludeKey === '' ? [] : excludeKey.split(',').map(Number));
        return (data.search_results?.data ?? []).filter(
            (row) => !(row.type === 'dashboard_element' && excluded.has(Number(row.id))),
        );
    }, [data.search_results, excludeKey]);
    const resolved = useResultItemsData(searchItems);
    const searchRows = useMemo(
        () => (resolved.settled ? renderableResults(searchItems, resolved) : []),
        [searchItems, resolved],
    );
    // A documentation row either carries the text of a section (the answer stands beside the turn's own) or, when the
    // kind door already served that text as the chat answer, only the blocks it was read from (cut S, D4473): then
    // the row is a source line, never a repeat of the answer.
    const docsSources = data.docs_answer?.data_sources ?? [];
    const hasDocsAnswer = !!data.docs_answer?.answer;
    const hasDocsSources = !hasDocsAnswer && !!data.docs_answer && docsSources.length > 0;
    const hasSearchResults = searchRows.length > 0;

    // The model's own reports draw as chips under the answer text (the streamed source block),
    // so the block's related_reports list has no section here any more (Marius, 2026-09-08).
    if (!hasDocsAnswer && !hasDocsSources && !hasSearchResults) {
        return null;
    }

    // The docs section is titled the way the tool chips are: with the Display Name and icon an
    // admin set in Concierge Setup for the block that produced it. The search section keeps that
    // block's icon but reads "Related Reports (n)", n being the rows that can render.
    const docsHeader = docsSectionHeader(docsSources, domains, t('message.docs_answer'));
    const searchHeader = domainSectionHeader('search', domains, t('message.related_reports'));
    const searchTitle = `${t('message.related_reports')} (${searchRows.length})`;

    return (
        <Stack gap="10px" sx={{ mt: '10px', width: '100%' }}>
            <DashedDivider />
            {hasDocsAnswer && (
                <CollapsibleSection
                    title={docsHeader.title}
                    icons={docsHeader.icons}
                    dataTest="additional_data_docs_answer_toggle"
                    defaultExpanded
                >
                    <Box
                        sx={{
                            ...defaultStyles.dialogItemText.messageText,
                            pt: '4px',
                            pl: 3.75,
                            color: (theme) => alpha(theme.palette.text.primary, 0.8),
                        }}
                        data-test="additional_data_docs_answer_text"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(data.docs_answer!.answer) }}
                    />
                </CollapsibleSection>
            )}
            {hasDocsSources && (
                <Box sx={{ ...labelSx }} data-test="additional_data_docs_answer_sources">
                    <SectionLabel
                        title={docsHeader.title}
                        icons={docsHeader.icons}
                        dataTest="additional_data_docs_answer_sources"
                    />
                </Box>
            )}
            {(hasDocsAnswer || hasDocsSources) && hasSearchResults && <DashedDivider />}
            {hasSearchResults && (
                <CollapsibleSection
                    title={searchTitle}
                    icons={searchHeader.icons}
                    dataTest="additional_data_search_results_toggle"
                >
                    <Box sx={{ pt: 0.75 }}>
                        <ResultItems items={searchRows} data={resolved} />
                    </Box>
                </CollapsibleSection>
            )}
        </Stack>
    );
}
